import { Monitor, Server, Terminal, Brain } from 'lucide-react';
import type { Runner } from '../types/runner';

interface SshHostOption {
  id: string;
  name: string;
  hostname: string;
  status: string;
}

export type RunTarget =
  | { mode: 'sdk' }
  | { mode: 'host'; runnerId: string; runnerName: string; hostname: string; platform?: string }
  | { mode: 'ssh'; sshHostId: string; sshName: string; hostname: string };

function isSelected(current: RunTarget, candidate: RunTarget): boolean {
  if (current.mode !== candidate.mode) return false;
  if (current.mode === 'host' && candidate.mode === 'host') {
    return current.runnerId === candidate.runnerId;
  }
  if (current.mode === 'ssh' && candidate.mode === 'ssh') {
    return current.sshHostId === candidate.sshHostId;
  }
  return true;
}

// eslint-disable-next-line react-refresh/only-export-components
export function getDefaultTarget(runners: Runner[], sshHosts: SshHostOption[]): RunTarget {
  try {
    const raw = localStorage.getItem('aldan-run-target');
    if (raw) {
      const parsed = JSON.parse(raw) as RunTarget;
      if (parsed.mode === 'host') {
        const r = runners.find((r) => r.id === parsed.runnerId && r.status === 'online');
        if (r) return parsed;
      } else if (parsed.mode === 'ssh') {
        const h = sshHosts.find((h) => h.id === parsed.sshHostId && h.status === 'connected');
        if (h) return parsed;
      } else if (parsed.mode === 'sdk') {
        return { mode: 'sdk' };
      }
    }
  } catch {
    // ignore
  }

  // Prefer non-LLM runner as default
  const runner =
    runners.find((r) => r.status === 'online' && !r.capabilities?.includes('local-llm')) ??
    runners.find((r) => r.status === 'online');
  if (runner) {
    return {
      mode: 'host',
      runnerId: runner.id,
      runnerName: runner.name || runner.hostname,
      hostname: runner.hostname,
      platform: runner.osInfo?.platform ?? undefined,
    };
  }

  const host = sshHosts.find((h) => h.status === 'connected');
  if (host) {
    return { mode: 'ssh', sshHostId: host.id, sshName: host.name, hostname: host.hostname };
  }

  return { mode: 'sdk' };
}

interface RunOnSelectorProps {
  value: RunTarget;
  onChange: (target: RunTarget) => void;
  runners: Runner[];
  sshHosts: SshHostOption[];
}

export default function RunOnSelector({ value, onChange, runners, sshHosts }: RunOnSelectorProps) {
  const onlineRunners = runners.filter((r) => r.status === 'online');
  const connectedHosts = sshHosts.filter((h) => h.status === 'connected');

  const handleChange = (target: RunTarget) => {
    try {
      localStorage.setItem('aldan-run-target', JSON.stringify(target));
    } catch {
      // ignore
    }
    onChange(target);
  };

  const pill = (selected: boolean) =>
    `flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all whitespace-nowrap ${
      selected
        ? 'border-slate-900 bg-slate-900 text-white'
        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
    }`;

  const sdkTarget: RunTarget = { mode: 'sdk' };

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">Run on</label>
      <div className="flex flex-wrap gap-2">
        {/* This server (SDK) */}
        <button
          type="button"
          onClick={() => handleChange(sdkTarget)}
          className={pill(isSelected(value, sdkTarget))}
        >
          <Server className="h-3 w-3" />
          This server
        </button>

        {/* Online runners */}
        {onlineRunners.map((runner) => {
          const isLlm = runner.capabilities?.includes('local-llm') ?? false;
          const Icon = isLlm ? Brain : Monitor;
          const target: RunTarget = {
            mode: 'host',
            runnerId: runner.id,
            runnerName: runner.name || runner.hostname,
            hostname: runner.hostname,
            platform: runner.osInfo?.platform ?? undefined,
          };
          const sel = isSelected(value, target);
          return (
            <button
              key={runner.id}
              type="button"
              onClick={() => handleChange(target)}
              className={pill(sel)}
              title={`${runner.hostname} · ${runner.osInfo?.platform ?? 'unknown'} · ${runner.assignedCount}/${runner.maxInstances} instances`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full shrink-0 ${sel ? 'bg-green-300' : 'bg-green-500'}`}
              />
              <Icon className="h-3 w-3 shrink-0" />
              {runner.name || runner.hostname}
              {isLlm && (
                <span className={`rounded px-1 text-[10px] ${sel ? 'bg-white/20' : 'bg-purple-100 text-purple-700'}`}>
                  LLM
                </span>
              )}
            </button>
          );
        })}

        {/* Connected SSH hosts */}
        {connectedHosts.map((host) => {
          const target: RunTarget = {
            mode: 'ssh',
            sshHostId: host.id,
            sshName: host.name,
            hostname: host.hostname,
          };
          return (
            <button
              key={host.id}
              type="button"
              onClick={() => handleChange(target)}
              className={pill(isSelected(value, target))}
              title={host.hostname}
            >
              <Terminal className="h-3 w-3 shrink-0" />
              {host.name}
            </button>
          );
        })}

        {onlineRunners.length === 0 && connectedHosts.length === 0 && (
          <p className="self-center text-xs text-slate-400">
            No runners connected —{' '}
            <a href="/runners" className="underline hover:text-slate-600">
              connect one
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
